import React, { useEffect, useState } from "react";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import Alert from "../../../components/v3/Alert";
import Empty from "../../../components/v3/Empty";
import Pill from "../../../components/v3/Pill";
import Btn from "../../../components/v3/Btn";
import Field from "../../../components/v3/Field";
import { bdcApi } from "../../../services/bdcApi";

/**
 * CampaignComposer — AD marketing campaigns (consultant-import WI-4).
 *
 * Compose (subject + HTML body + audience) → save DRAFT → preview the
 * recipient count → send (one-shot: DRAFT→ENQUEUED server-side) → watch the
 * delivery rollup, which is derived from email_logs so it always agrees with
 * the Email log viewer. Endpoints sit behind featureGate
 * ('feature.marketing_email') — a 503 here means the kill switch is off.
 */

const AUDIENCE_LABELS = {
  SUBSCRIBERS: "Newsletter subscribers (double-opted-in)",
  OPTED_IN_USERS: "Registered users opted into the newsletter",
  ALL: "Both (deduped)",
};

const STATUS_TONE = { DRAFT: "amber", ENQUEUED: "mint", CANCELLED: "gray" };

function CampaignComposer() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);
  const [campaigns, setCampaigns] = useState([]);

  const [form, setForm] = useState({ subject: "", body_html: "", audience: "ALL" });
  const [submitting, setSubmitting] = useState(false);

  // Per-campaign transient state keyed by id.
  const [previews, setPreviews] = useState({});
  const [statuses, setStatuses] = useState({});
  const [busyId, setBusyId] = useState(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await bdcApi.adListCampaigns({ limit: 25 });
      setCampaigns(res?.data?.rows || []);
      setError(null);
    } catch (err) {
      setError(
        err?.response?.status === 503
          ? "Marketing email is switched off (feature.marketing_email)."
          : err?.response?.data?.message || err.message || "Failed to load"
      );
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function createDraft(e) {
    e.preventDefault();
    setSubmitting(true);
    setFlash(null);
    try {
      await bdcApi.adCreateCampaign(form);
      setFlash({ tone: "info", text: "Draft saved." });
      setForm({ subject: "", body_html: "", audience: "ALL" });
      await refresh();
    } catch (err) {
      setFlash({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Save failed",
      });
    } finally {
      setSubmitting(false);
    }
  }

  async function previewCount(id) {
    setBusyId(id);
    try {
      const res = await bdcApi.adCampaignPreviewCount(id);
      setPreviews((p) => ({ ...p, [id]: res?.data }));
    } catch (err) {
      setFlash({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Preview failed",
      });
    } finally {
      setBusyId(null);
    }
  }

  async function sendCampaign(id) {
    const preview = previews[id];
    const count = preview ? `${preview.recipients} recipient(s)` : "the full audience";
    // Outward-facing + irreversible — always confirm.
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Send this campaign to ${count}? This cannot be undone.`)) return;
    setBusyId(id);
    setFlash(null);
    try {
      const res = await bdcApi.adSendCampaign(id);
      setFlash({
        tone: "info",
        text: `Campaign enqueued to ${res?.data?.enqueued_count} recipient(s). The mail queue drains at the marketing rate cap.`,
      });
      await refresh();
      await checkStatus(id);
    } catch (err) {
      setFlash({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Send failed",
      });
    } finally {
      setBusyId(null);
    }
  }

  async function checkStatus(id) {
    setBusyId(id);
    try {
      const res = await bdcApi.adCampaignStatus(id);
      setStatuses((s) => ({ ...s, [id]: res?.data }));
    } catch (err) {
      setFlash({
        tone: "danger",
        text: err?.response?.data?.message || err.message || "Status fetch failed",
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <PageShell>
      <Loader isLoading={loading} />
      <PageHead title="Marketing campaigns" />

      {error && <Alert tone="danger">{error}</Alert>}
      {flash && <Alert tone={flash.tone}>{flash.text}</Alert>}

      <section className="card">
        <div className="card__head">
          <div className="card__title">Compose campaign</div>
        </div>
        <div className="card__body">
          <form onSubmit={createDraft} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field
              label="Subject *"
              name="campaign-subject"
              required
              maxLength={255}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
            />
            <Field
              as="select"
              label="Audience"
              name="campaign-audience"
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
            >
              {Object.entries(AUDIENCE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Field>
            <Field
              as="textarea"
              label="Body (HTML) *"
              name="campaign-body"
              rows={10}
              required
              value={form.body_html}
              onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              placeholder="<p>Hello,</p><p>…</p> — wrapped in the branded header/footer (with unsubscribe link) at send time."
            />
            <Btn variant="primary" type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save draft"}
            </Btn>
          </form>
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <div className="card__title">Campaigns</div>
        </div>
        <div className="card__body card__body--flush">
          {campaigns.length === 0 ? (
            <Empty>No campaigns yet.</Empty>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Subject</th>
                  <th>Audience</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Recipients</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => {
                  const preview = previews[c.id];
                  const stat = statuses[c.id];
                  return (
                    <React.Fragment key={c.id}>
                      <tr>
                        <td>{c.subject}</td>
                        <td className="muted">{c.audience}</td>
                        <td><Pill tone={STATUS_TONE[c.status] || "gray"}>{c.status}</Pill></td>
                        <td className="muted">{new Date(c.createdAt).toLocaleDateString()}</td>
                        <td className="muted">
                          {c.status === "DRAFT"
                            ? (preview ? `${preview.recipients} (+${preview.suppressed} suppressed)` : "—")
                            : c.enqueued_count}
                        </td>
                        <td style={{ display: "flex", gap: 6 }}>
                          {c.status === "DRAFT" && (
                            <>
                              <Btn size="sm" type="button" disabled={busyId === c.id}
                                onClick={() => previewCount(c.id)}>
                                Preview count
                              </Btn>
                              <Btn size="sm" variant="primary" type="button" disabled={busyId === c.id}
                                onClick={() => sendCampaign(c.id)}>
                                Send
                              </Btn>
                            </>
                          )}
                          {c.status === "ENQUEUED" && (
                            <Btn size="sm" type="button" disabled={busyId === c.id}
                              onClick={() => checkStatus(c.id)}>
                              Delivery status
                            </Btn>
                          )}
                        </td>
                      </tr>
                      {stat && (
                        <tr>
                          <td colSpan={6} className="muted" style={{ fontSize: "var(--t-sm)" }}>
                            Delivery — queued {stat.delivery.queued} · sent {stat.delivery.sent} ·
                            failed {stat.delivery.failed} · dead-letter {stat.delivery.dead_letter} ·
                            processing {stat.delivery.processing}
                            {" "}(details in the Email log viewer)
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </PageShell>
  );
}

export default CampaignComposer;
